import type { AgentDraftGoal } from "@/shared/domain/agents";
import {
  ConfirmationBullets,
  ConfirmationCard,
  ConfirmationField,
  ConfirmationFields,
  ConfirmationLead,
  ConfirmationNote,
  ConfirmationPanel,
} from "@/shared/foundation/primitives";

import {
  previewAgentProposal,
  type ClientCreateAgentInput,
} from "./clientCreateAgentCoerce";
import type { ConfirmationContentProps } from "./ClientTools";

function previewKeys(keys: string[]): string {
  return keys.length <= 3
    ? keys.join(", ")
    : `${keys.slice(0, 3).join(", ")}, +${keys.length - 3} more`;
}

function summarizeGoal(goal: AgentDraftGoal): string {
  switch (goal.goal_type) {
    case "dataset_summary":
      return "Dataset summary";
    case "dataset_triage":
      // Normalization guarantees a non-empty vocabulary — a triage goal with
      // no usable labels is dropped rather than carried through as empty.
      return `Dataset triage: ${previewKeys(Object.keys(goal.label_vocabulary))}`;
    case "create_events": {
      const suffix = goal.collection_enabled === true ? " → collection" : "";
      return `Create events: ${previewKeys(Object.keys(goal.event_vocabulary))}${suffix}`;
    }
  }
}

/**
 * Read-only preview of the draft the model wants to seed into the Agent
 * editor. Surfaces the proposed name (or the agent being updated), a short
 * description if provided, the goals, and whether a kickoff message is
 * included. The editor itself is the rich confirmation surface — this card
 * just gives the user enough signal to decide whether to approve the
 * navigation when Auto is off.
 *
 * Everything shown comes from {@link previewAgentProposal}, the same
 * normalization the tool's `execute` runs, so the card can't advertise a goal
 * or variable that the editor won't actually receive.
 */
export function CreateAgentConfirmation({
  params,
}: ConfirmationContentProps<ClientCreateAgentInput>) {
  const { targetAgentName, draft, skillNames } = previewAgentProposal(params);
  const isUpdate = targetAgentName !== undefined;
  // `coerceToAgentDraft` returns null only for a create with no usable name,
  // which `execute` rejects outright; render the empty shape so this stays a
  // pure preview rather than a second validation seam.
  const proposal = draft ?? {};
  const goals = proposal.goals ?? [];
  const variableCount = proposal.variables?.length ?? 0;
  const heading = isUpdate
    ? `Update ${targetAgentName}`
    : `Create ${proposal.name ?? "(unnamed)"}`;

  return (
    <ConfirmationPanel label="Create agent preview">
      <ConfirmationLead>
        {isUpdate
          ? "Open the Agent editor to update this agent:"
          : "Open the Agent editor pre-filled with this draft:"}
      </ConfirmationLead>
      <ConfirmationCard title={heading}>
        <ConfirmationFields>
          {/* On an update a supplied `name` is a rename, so it's a consequence
              of approving and has to be visible — the heading names the agent
              being changed, not the new name. */}
          {isUpdate && proposal.name !== undefined && (
            <ConfirmationField label="Rename to">
              {proposal.name}
            </ConfirmationField>
          )}
          {proposal.description !== undefined && (
            <ConfirmationField label="Description">
              {proposal.description}
            </ConfirmationField>
          )}
          {proposal.model_profile !== undefined && (
            <ConfirmationField label="Model">
              {proposal.model_profile}
            </ConfirmationField>
          )}
          {proposal.kickoff_message !== undefined && (
            <ConfirmationField label="Kickoff">
              {proposal.kickoff_message}
            </ConfirmationField>
          )}
          {goals.length > 0 && (
            <ConfirmationField label={`Goal${goals.length === 1 ? "" : "s"}`}>
              <ConfirmationBullets>
                {goals.map((goal, idx) => (
                  <li key={idx}>{summarizeGoal(goal)}</li>
                ))}
              </ConfirmationBullets>
            </ConfirmationField>
          )}
          {/* Skills are shown by name, which is what the model proposed and
              what the user recognizes; `execute` resolves them to ids and
              errors on any it can't attach, so nothing here can be silently
              dropped. An explicit empty list is a real instruction (detach
              everything), so it renders rather than disappearing. */}
          {skillNames !== undefined && (
            <ConfirmationField label="Skills">
              {skillNames.length === 0 ? "Detach all" : skillNames.join(", ")}
            </ConfirmationField>
          )}
          {variableCount > 0 && (
            <ConfirmationField label="Variables">
              {variableCount}
            </ConfirmationField>
          )}
        </ConfirmationFields>
      </ConfirmationCard>
      <ConfirmationNote>
        {isUpdate
          ? "Approving opens the existing agent in the editor with these changes prefilled — nothing is saved until you review and click Save changes there."
          : "Approving navigates you to the editor — the agent isn’t saved until you click Create there."}
      </ConfirmationNote>
    </ConfirmationPanel>
  );
}
